import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className="privacy-policy">
            {/* Hero Section */}
           <section className="bg-image pt-40 pb-40">
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-duration="1.2s" data-wow-delay=".2s">Privacy Policy</h2>
                </div>
            </section>

            {/* Main Content */}
            <div className="privacy-content max-w-5xl mx-auto px-4 py-12">
                {/* Introduction */}
                <section className="mb-12">
                    <p className="text-gray-700 leading-relaxed mb-6">
                        Aranyasetu ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by Aranyasetu.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                        This Privacy Policy applies to our website and mobile applications, and its associated subdomains (collectively our "Service"). By accessing or using our Service, you signify that you have read, understand, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service.
                    </p>
                </section>

                {/* Definitions Section */}
                <SectionBlock 
                    title="Definitions and Key Terms"
                    content={
                        <>
                            <p className="mb-4">To help explain things as clearly as possible in this Privacy Policy, every time any of these terms are referenced, are strictly defined as:</p>
                            <DefinitionList items={[
                                { term: "Cookie", definition: "Small amount of data generated by a website and saved by your web browser. It is used to identify your browser, provide analytics, remember information about you such as your language preference or login information." },
                                { term: "Company", definition: "When this policy mentions 'Company', 'we', 'us', or 'our', it refers to Aranyasetu located at 407 Payal Complex, B/s VSE, Sayajigunj, Vadodara-390005, Gujarat (India) is responsible for your information under this Privacy Policy." },
                                { term: "Country", definition: "Where Aranyasetu or the owners/founders of Aranyasetu are based, in this case is India." },
                                { term: "Customer", definition: "Refers to the company, organization or person that signs up to use the Aranyasetu Service to manage the relationships with your consumers or service users." },
                                { term: "Device", definition: "Any internet connected device such as a phone, tablet, computer or any other device that can be used to visit Aranyasetu and use the services." },
                                { term: "IP address", definition: "Every device connected to the internet is assigned a number known as an Internet protocol (IP) address. These numbers are usually assigned in geographic blocks. An IP address can often be used to identify the location from which a device is connecting to the internet." },
                                { term: "Personnel", definition: "Refers to those individuals who are employed by Aranyasetu or are under contract to perform a service on behalf of one of the parties." },
                                { term: "Personal Data", definition: "Any information that directly, indirectly, or in connection with other information - including a personal identification number - allows for the identification or identifiability of a natural person." },
                                { term: "Service", definition: "Refers to the service provided by Aranyasetu as described in the relative terms (if available) and on this platform." },
                                { term: "Third-party service", definition: "Refers to advertisers, contest sponsors, promotional and marketing partners, and others who provide our content or whose products or services we think may interest you." },
                                { term: "Website", definition: "Aranyasetu's site, which can be accessed via the URL: www.aranyasetu.org" },
                                { term: "You", definition: "A person or entity that is registered with Aranyasetu to use the services." }
                            ]} />
                        </>
                    }
                />

                {/* Information Collection */}
                <SectionBlock 
                    title="What Information Do We Collect?"
                    content={
                        <>
                            <p className="mb-4">We collect information from you when you visit our website, register on our site, place an order, subscribe to our newsletter, respond to a survey or fill out a form.</p>
                            <ul className="list-disc pl-6 space-y-2 mb-4">
                                <li>Name / Username</li>
                                <li>Phone Numbers</li>
                                <li>Email Addresses</li>
                                <li>Password</li>
                                <li>
                                    We also collect optional mobile device information:
                                    <ul className="list-disc pl-6 mt-2 space-y-2">
                                        <li><strong className='font-bold'>Location (GPS):</strong> Helps deliver targeted and relevant ads based on your interests.</li>
                                        <li><strong className='font-bold'>Camera (Pictures):</strong> Allows uploading pictures directly from the camera. This permission is optional and can be safely denied.</li>
                                        <li><strong className='font-bold'>Photo Gallery (Pictures):</strong> Allows uploading pictures from your gallery. This permission is also optional and can be safely denied.</li>
                                    </ul>
                                </li>
                            </ul>
                        </>
                    }
                />

                {/* Third Party Information */}
                <SectionBlock 
                    title="When does Aranyasetu use end user information from third parties?"
                    content="Aranyasetu will collect End User Data necessary to provide the Aranyasetu services to our customers. End users may voluntarily provide us with information they have made available on social media websites. If you provide us with any such information, we may collect publicly available information from social media websites you have indicated. You can control how much of your information social media websites make public by visiting these websites and changing your privacy settings."
                />

                {/* Sharing Information */}
                <SectionBlock 
                    title="Do we share the information we collect with third parties?"
                    content="We Do Not share the information that we collect, both personal and non-personal, with third parties such as advertisers, contest sponsors, promotional and marketing partners, and others who provide our content or whose products or services we think may interest you. We may share it with our current and future affiliated companies and business partners if we are involved in a merger, asset sale or other business recognition, we may also share or transfer your personal and non-personal information to our successors-in-interest."
                />

                {/* Collection Points */}
                <SectionBlock 
                    title="Where and when is information collected from customers and end users?"
                    content="Aranyasetu will collect personal information that you submit to us. We may also receive personal information about you from third parties as described above."
                />

                {/* How We Use Information */}
                <SectionBlock 
                    title="How Do We Use The Information We Collect?"
                    content={
                        <>
                            <p className="mb-4">Any of the information we collect from you may be used in one of the following ways:</p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li className='font-bold'>To personalize your experience (your information helps us to better respond to your individual needs)</li>
                                <li className='font-bold'>To improve our website (we continually strive to improve our website offerings based on the information and feedback we receive from you)</li>
                                <li className='font-bold'>To improve customer service (your information helps us to more effectively respond to your customer service requests and support needs)</li>
                                <li className='font-bold'>To process transactions</li>
                                <li className='font-bold'>To administer a contest, promotion, survey or other site feature</li>
                                <li className='font-bold'>To send periodic emails</li>
                            </ul>
                        </>
                    }
                />

                {/* Email Usage */}
                <SectionBlock 
                    title="How Do We Use Your Email Address?"
                    content="By submitting your email address on this website, you agree to receive emails from us. We only send emails to people who have authorized us to contact them, either directly, or through a third party. We do not send unsolicited commercial emails, because we hate spam as much as you do. If, however, you have provided the same email to us through another method, we may use it for any of the purposes stated in this Policy."
                />

                {/* Data Retention */}
                <SectionBlock 
                    title="How Long Do We Keep Your Information?"
                    content="We keep your information only so long as we need it to provide Aranyasetu to you and fulfill the purposes described in this policy. This is also the case for anyone that we share your information with and who carries out services on our behalf. When we no longer need to use your information and there is no need for us to keep it to comply with our legal or regulatory obligations, we'll either remove it from our systems or depersonalize it so that we can't identify you."
                />

                {/* Information Protection */}
                <SectionBlock 
                    title="How Do We Protect Your Information?"
                    content="We implement a variety of security measures to maintain the safety of your personal information when you submit, or access your personal information. We offer the use of a secure server. All supplied sensitive/credit information is transmitted via Secure Socket Layer (SSL) technology and then encrypted are required to keep the information confidential."
                />

                {/* International Transfer */}
                <SectionBlock 
                    title="Could my information be transferred to other countries?"
                    content="Aranyasetu is incorporated in India. Information collected via our website, through direct interactions with you, or from use of our help services may be transferred from time to time to our offices or personnel, or to third parties, located throughout the world, and may be viewed and hosted anywhere in the world, including countries that may not have laws of general applicability regulating the use and transfer of such data."
                />

                {/* Security */}
                <SectionBlock 
                    title="Is the information collected through the Aranyasetu Service secure?"
                    content="We take precautions to protect the security of your information. We have physical, electronic, and managerial procedures to help safeguard, prevent unauthorized access, maintain data security, and correctly use your information. However, neither people nor security systems are foolproof, including encryption systems."
                />

                {/* Information Update */}
                <SectionBlock 
                    title="Can I update or correct my information?"
                    content="The rights you have to request updates or corrections to the information Aranyasetu collects depend on your relationship with Aranyasetu. Personnel may update or correct their information as detailed in our internal company policies. Customers have the right to request the restriction of certain users and disclosures of personally identifiable information."
                />

                {/* Sale of Business */}
                <SectionBlock 
                    title="Sale of Business"
                    content="We reserve the right to transfer information to a third party in the event of a sale, merger or other transfer of all or substantially all of the assets of Aranyasetu or any of its Corporate Affiliates, or that portion of Aranyasetu or any of its Corporate Affiliates to which the Service relates, or in the event that we discontinue our business or file a petition or have filed against us a petition in bankruptcy, reorganization or similar proceeding, provided that the third party agrees to adhere to the terms of this Privacy Policy."
                />

                {/* Affiliates */}
                <SectionBlock 
                    title="Affiliates"
                    content="We may disclose information (including personal information) about you to our Corporate Affiliates. For purposes of this Privacy Policy, 'Corporate Affiliate' means any person or entity which directly or indirectly controls, is controlled by or is under common control with Aranyasetu, whether by ownership or otherwise."
                />

                {/* Governing Law */}
                <SectionBlock 
                    title="Governing Law"
                    content="This Privacy Policy is governed by the laws of India without regard to its conflict of laws provision. You consent to the exclusive jurisdiction of the courts in connection with any action or dispute arising between the parties under or in connection with this Privacy Policy."
                />

                {/* Consent */}
                <SectionBlock 
                    title="Your Consent"
                    content="We've updated our Privacy Policy to provide you with complete transparency into what is being set when you visit our site and how it's being used. By using our Aranyasetu, registering an account, or making a purchase, you hereby consent to our Privacy Policy and agree to its terms."
                />

                {/* Cookies */}
                <SectionBlock 
                    title="Cookies"
                    content="Aranyasetu uses 'Cookies' to identify the areas of our website that you have visited. A Cookie is a small piece of data stored on your computer or mobile device by your web browser. We use Cookies to enhance the performance and functionality of our website but are non-essential to their use."
                />

                {/* Blocking Cookies */}
                <SectionBlock 
                    title="Blocking and disabling cookies and similar technologies"
                    content="Wherever you're located you may also set your browser to block cookies and similar technologies, but this action may block our essential cookies and prevent our website from functioning properly, and you may not be able to fully utilize all of its features and services."
                />

                {/* Payment Details */}
                <SectionBlock 
                    title="Payment Details"
                    content="We are neither collecting nor asking for any payment or relevant information on our site or app. So in case you come to know about any such incident, please play the role of a whistleblower and bring those attempts to our notice to protect you financially."
                />

                {/* Kids' Privacy */}
                <SectionBlock 
                    title="Kids' Privacy"
                    content="We have not put any age restriction for our users. If you are a parent or guardian and you are aware that your child has provided us with personal data without your permission, please contact us if you want to remove any such data. We are open to provide support."
                />

                {/* Changes */}
                <SectionBlock 
                    title="Changes to Our Privacy Policy"
                    content="We may change our service and policies, and we may need to make changes to this Privacy Policy so that they accurately reflect our service and policies. Unless otherwise required by law, we will notify you before we make changes to this Privacy Policy and give you an opportunity to review them before they go into effect."
                />

                {/* Third-Party Services */}
                <SectionBlock 
                    title="Third-Party Services"
                    content="We may display, include or make available third-party content (including data, information, applications and other products or services) or provide links to third-party websites or services ('Third-Party Services'). You acknowledge and agree that Aranyasetu shall not be responsible for any Third-Party Services."
                />

                {/* Tracking Technologies */}
                <SectionBlock 
                    title="Tracking Technologies"
                    content={
                        <>
                            <h3 className="text-xl font-semibold mt-4 mb-2">Local Storage</h3>
                            <p className="mb-4">Local Storage, sometimes known as DOM storage, provides web apps with methods and protocols for storing client-side data. Web storage supports persistent data storage, similar to cookies but with a greatly enhanced capacity and no information stored in the HTTP request header.</p>
                            
                            <h3 className="text-xl font-semibold mt-4 mb-2">Sessions</h3>
                            <p>Aranyasetu uses 'Sessions' to identify the areas of our website that you have visited. A Session is a small piece of data stored on your computer or mobile device by your web browser.</p>
                        </>
                    }
                />

                {/* Contact */}
                  <h2 className="text-2xl font-semibold mb-4 mt-4">Contact Us</h2>
                    <p className="mb-6 text-base leading-relaxed">
                      Don't hesitate to contact us if you have any questions.
                        <br /><br />
                        Aranyasetu.org<br />
                        407 Payal Complex, B/s VSE<br />
                        Sayajigunj, Vadodara-390005<br />
                        Gujarat, India<br />
                        Email: support@aranyasetu.org<br />
                        Website: www.aranyasetu.org
                    </p>
            </div>
        </div>
    );
};

// Reusable components for better structure
const SectionBlock = ({ title, content }) => (
    <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{title}</h2>
        {typeof content === 'string' ? (
            <p className="text-gray-700 leading-relaxed">{content}</p>
        ) : (
            <div className="text-gray-700 leading-relaxed">{content}</div>
        )}
    </section>
);

const DefinitionList = ({ items }) => (
    <ul className="list-disc pl-6 space-y-4">
        {items.map((item, index) => (
            <li className="font-bold" key={index}>
                <span className="font-bold text-gray-800">{item.term}:</span> {item.definition}
            </li>
        ))}
    </ul>
);

export default PrivacyPolicy;